<script setup lang="ts">
/**
 * ☁️ Shown only when two devices have BOTH moved a run on since they last
 * agreed (v3.60). Every other case resolves silently — this is the one
 * where guessing would throw away real play, so the player picks.
 *
 * Deliberately not dismissable by tapping outside: the two runs cannot
 * both survive, and an accidental backdrop tap should not decide which.
 *
 * 🪦 v6.38: EITHER side may be a tombstone — a device that finished,
 * lost or abandoned the run (v6.37). The copy branches on which, so a
 * button never promises a run that does not exist: "keep this device's
 * run" over a fabricated `Act 0 · 0/0 HP` statline was, in truth, a
 * button that wiped the other device's live run.
 */
const { conflict, keepLocal, takeRemote } = useRunSync()
const { t } = useLocale()
const sfx = useSfx()
// 📱 M6: Android's back waits here (useBackStack) - which run to keep is a
// choice only the player's own tap makes.
useBackLayer('run-conflict', () => !!conflict.value, null)

/**
 * Which story the card tells. `both` is the v3.60 original; the other
 * three exist because a run that ENDED on one side has no statline and
 * no "keep it" — only an ending to confirm or overrule.
 */
const kind = computed<'both' | 'remoteEnded' | 'localEnded' | 'bothEnded'>(() => {
  const c = conflict.value
  if (!c) return 'both'
  if (c.hasLocalRun) return c.remoteRun ? 'both' : 'remoteEnded'
  // The resolver should adopt a tombstone that meets a tombstone rather
  // than open this; if it ever does, both buttons end the run and the
  // copy says so instead of inventing a disagreement.
  return c.remoteRun ? 'localEnded' : 'bothEnded'
})

function ago(when: number | null): string {
  if (!when) return t('sync.unknownTime')
  const secs = Math.max(0, Math.round((Date.now() - when) / 1000))
  if (secs < 90) return t('sync.agoS', { n: secs })
  const mins = Math.round(secs / 60)
  if (mins < 90) return t('sync.agoMin', { n: mins })
  const hours = Math.round(mins / 60)
  return hours < 36 ? t('sync.agoH', { n: hours }) : t('sync.agoD', { n: Math.round(hours / 24) })
}

/**
 * 🪦 v6.38: keyed on the REAL RunPhase values. The old list named
 * `reward` and `done`, which no run ever carries, and missed `bonus`
 * and `summary`, which every run passes through — those leaked raw. So
 * does summarizeRun's `none` for a side with no run, hence its own
 * word. Anything still unknown is a future phase; it shows as-is rather
 * than as a bare `sync.phase.x` key.
 */
const PHASE_KEY: Record<string, string> = {
  draft: 'draft', bonus: 'draft', summary: 'draft',
  map: 'map', encounter: 'node',
  reward: 'reward', done: 'done', none: 'none',
}
function phaseWord(phase: string): string {
  const key = PHASE_KEY[phase]
  return key ? t(`sync.phase.${key}`) : phase
}

function summary(side: { act: number; hp: number; maxHp: number; phase: string; roster: number }): string {
  return t('sync.summary', { act: side.act, hp: side.hp, max: side.maxHp, phase: phaseWord(side.phase), n: side.roster })
}

function onKeep() {
  sfx.confirm()
  void keepLocal()
}
function onTake() {
  sfx.confirm()
  takeRemote()
}
</script>

<template>
  <div v-if="conflict" class="sync-backdrop" data-testid="run-conflict">
    <div class="card sync-card">
      <h2 class="sync-title">{{ t(kind === 'both' ? 'sync.title' : `sync.title.${kind}`) }}</h2>
      <p class="sync-note">{{ t(kind === 'both' ? 'sync.body' : `sync.body.${kind}`) }}</p>

      <!-- 🪦 v6.38: with no local run, "keep this device's run" is a
           tombstone push wearing a live run's label. Say what it does:
           this device ended the run, and picking it ends the run
           everywhere. No statline — there is nothing to summarise. -->
      <button class="sync-option" data-testid="sync-keep-local" @click="onKeep">
        <strong>{{ conflict.hasLocalRun ? t('sync.keepLocal') : t('sync.endEverywhere') }}</strong>
        <span class="sync-detail">{{ conflict.hasLocalRun ? summary(conflict.local) : t('sync.localEnded') }}</span>
        <span v-if="conflict.hasLocalRun" class="sync-when">{{ t('sync.justNow') }}</span>
      </button>

      <!-- 🪦 v6.37: the other side may be a TOMBSTONE - that device
           finished, lost or abandoned the run. There is no statline to
           show, and taking it means accepting the ending. -->
      <button class="sync-option" data-testid="sync-take-remote" @click="onTake">
        <strong>{{ conflict.remoteRun ? t('sync.takeRemote') : t('sync.acceptEnded') }}</strong>
        <span class="sync-detail">{{ conflict.remoteRun ? summary(conflict.remote) : t('sync.remoteEnded') }}</span>
        <span class="sync-when">{{ conflict.remote.device }} · {{ ago(conflict.remote.when) }}</span>
      </button>

      <p class="sync-warn">{{ t('sync.discarded') }}</p>
    </div>
  </div>
</template>

<style scoped>
.sync-backdrop {
  position: fixed;
  inset: 0;
  z-index: 80;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(12, 26, 48, 0.72);
  backdrop-filter: blur(2px);
  touch-action: none;
}
.sync-card {
  width: min(380px, 100%);
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: center;
}
.sync-title {
  margin: 0;
  font-size: 1.2rem;
}
.sync-note {
  margin: 0;
  font-size: 0.82rem;
  color: var(--color-ink-soft);
}
.sync-option {
  display: flex;
  flex-direction: column;
  gap: 3px;
  padding: 10px 12px;
  border: 1px solid rgba(31, 58, 95, 0.25);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.85);
  font: inherit;
  color: var(--color-ink);
  text-align: left;
  cursor: pointer;
}
.sync-option:hover {
  border-color: var(--color-gold-deep);
  background: #fff8e5;
}
.sync-detail {
  font-size: 0.78rem;
  color: var(--color-ink);
}
.sync-when {
  font-size: 0.72rem;
  color: var(--color-ink-soft);
}
.sync-warn {
  margin: 2px 0 0;
  font-size: 0.72rem;
  color: var(--color-ink-soft);
}
</style>
